import startCase from 'lodash/startCase';
import { useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { Checkbox } from 'ui/checkboxV2';
import { FormField } from 'ui/formField';
import { InputText, TextArea } from 'ui/inputs';
import { Label, colors, monospaceCaption, spacing } from 'ui/theme';
import noop from 'utils/noop';
import { isDefined } from 'utils/tsHelpers';
import { sendRequest } from '../actions';
import { selectAdminRoutes } from '../selectors';
import { Inputs } from '../types';
import { adminEndpointsFormStateReducer, getListOfNecessaryCookies } from '../utils';

export const CodeBlock = styled.pre`
  ${monospaceCaption};
  max-height: ${spacing.x16};
  overflow: auto;
  padding: ${spacing.eighth} ${spacing.quarter};
  color: ${colors.textPrimary};
  background: ${colors.backgroundTertiary};
  border: 1px solid ${colors.borderPrimary};
  border-radius: ${spacing.quarter};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};

  &:not(:only-child) {
    margin-top: ${spacing.base};
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  width: max(25%, 5rem);
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

const AdminEndpointForm = ({ actions, route, opts, method }) => {
  const initialState = Object.entries(opts).reduce((state, [fieldName, valueOpts]) => {
    const initialValue =
      valueOpts.type === Inputs.Checkbox ? Boolean(valueOpts.default) : (valueOpts?.default ?? '');

    return {
      ...state,
      [fieldName]: initialValue,
    };
  }, []);
  const [state, stateDispatch] = useReducer(adminEndpointsFormStateReducer, initialState);

  const sagaDispatch = useDispatch();
  const adminRoutes = useSelector(selectAdminRoutes);
  const currentRouteResponse = adminRoutes?.[route];

  const handleSubmit = () => {
    const cookies = getListOfNecessaryCookies();
    sagaDispatch(
      sendRequest({
        actions,
        data: state,
        cookies,
        route,
        method,
      }),
    );
  };

  return (
    <Container data-test="adminEndpointForm">
      {Object.entries(opts).map(([key, { helpText, label, restrictionText, type }]) => {
        const formKey = `${route}-${key}`;
        return type === 'checkbox' ? (
          <FormField key={formKey} inputId={key}>
            <Checkbox
              id={key}
              data-test={`adminEndpoint-${formKey}`}
              onClick={() => stateDispatch({ name: key, type: 'checkbox', value: !state[key] })}
              status={!!state[key]}
              label={startCase(key)}
            />
          </FormField>
        ) : (
          <FormField
            key={formKey}
            inputId={key}
            labelText={label || startCase(key)}
            helpText={helpText}
            restrictionText={restrictionText}
          >
            {type === Inputs.TextArea ? (
              <TextArea
                onChange={(val) => {
                  const value = val.split(',');
                  stateDispatch({ name: key, type: 'text', value });
                }}
                value={state[key].toString()}
              />
            ) : (
              <InputText
                id={key}
                data-test={`adminEndpoint-${formKey}`}
                onChange={(value) => stateDispatch({ name: key, type: 'text', value })}
                value={state[key]}
              />
            )}
          </FormField>
        );
      })}
      <Actions>
        <StyledPrimaryButton data-test={`adminEndpoint-submit-${route}`} onClick={handleSubmit}>
          Submit
        </StyledPrimaryButton>
      </Actions>

      {Boolean(isDefined(actions) && currentRouteResponse) && (
        <>
          <Label style={{ marginTop: spacing.base }}>Response</Label>
          <CodeBlock>
            <code>{JSON.stringify(currentRouteResponse, undefined, 2)}</code>
          </CodeBlock>
          <Actions>
            {actions.map(({ onClick = noop, title = 'Download' }) => (
              <SecondaryButton key={title} onClick={() => onClick(currentRouteResponse)}>
                {title}
              </SecondaryButton>
            ))}
          </Actions>
        </>
      )}
    </Container>
  );
};

export default AdminEndpointForm;
